/**
 * Reaction Tap: level params and trial generation (spec v2.1 Game 9).
 * Levels 1–2: single green centered, 10 stimuli.
 * Levels 3–4: green at random position (3 zones), 10 stimuli.
 * Levels 5–6: go/no-go (green tap, red don't), 12 stimuli, 60% go (max 3 consecutive red).
 * Levels 7–8: go/no-go + grey distractors, 15 stimuli.
 * Levels 9–10: multiple circles, tap green only, 15 stimuli.
 */

export type StimulusType = "go" | "nogo" | "distractor";

export interface TrialSpec {
  type: StimulusType;
  /** 0 = center; 1–3 = zones for single target; for multi-target, positions are in positions[] */
  zoneIndex: number;
  /** For level 9–10: multiple circles at once; only one is go (green), others nogo/distractor */
  positions?: { xPct: number; yPct: number; type: StimulusType }[];
}

export interface LevelParams {
  stimulusCount: number;
  /** 0 = center only; 3 = three zones; 9 = full grid for multi-target */
  zoneCount: number;
  goNoGo: boolean;
  hasDistractors: boolean;
  multiTarget: boolean;
  /** ms to tap go stimulus before counting as missed */
  maxReactionMs: number;
  /** min delay before next stimulus (ms) */
  minDelayMs: number;
  /** max delay before next stimulus (ms) */
  maxDelayMs: number;
  /** go trials as fraction (e.g. 0.6 = 60% go) */
  goFraction: number;
}

/** Seeded RNG in [0, 1) */
function seededRandom(seed: string): () => number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h << 5) - h + seed.charCodeAt(i);
    h |= 0;
  }
  return () => {
    h = (h * 1103515245 + 12345) & 0x7fffffff;
    return h / 0x7fffffff;
  };
}

function pickInt(rng: () => number, min: number, max: number): number {
  return min + Math.floor(rng() * (max - min + 1));
}

/** Max consecutive nogo (red) trials after shuffling; reduces clustering while keeping go/nogo counts. */
const MAX_CONSECUTIVE_NOGO = 3;

function shuffleGoMask(isGo: boolean[], rng: () => number): void {
  for (let i = isGo.length - 1; i > 0; i--) {
    const j = pickInt(rng, 0, i);
    const t = isGo[i];
    isGo[i] = isGo[j];
    isGo[j] = t;
  }
}

/** Mutates `isGo` so no more than `maxRun` consecutive false; preserves counts. */
function capConsecutiveNogo(isGo: boolean[], maxRun: number): void {
  const n = isGo.length;
  const needLen = maxRun + 1;
  for (let iter = 0; iter < 2000; iter++) {
    let badStart: number | null = null;
    for (let i = 0; i <= n - needLen; i++) {
      let allNogo = true;
      for (let k = 0; k < needLen; k++) {
        if (isGo[i + k]) {
          allNogo = false;
          break;
        }
      }
      if (allNogo) {
        badStart = i;
        break;
      }
    }
    if (badStart === null) return;

    const swapNogoIdx = badStart + maxRun;
    let partner = -1;
    for (let j = swapNogoIdx + 1; j < n; j++) {
      if (isGo[j]) {
        partner = j;
        break;
      }
    }
    if (partner < 0) {
      for (let j = badStart - 1; j >= 0; j--) {
        if (isGo[j]) {
          partner = j;
          break;
        }
      }
    }
    if (partner < 0) return;
    isGo[swapNogoIdx] = true;
    isGo[partner] = false;
  }
}

export function getLevelParams(level: number): LevelParams {
  const clamped = Math.max(1, Math.min(10, Math.round(level)));
  if (clamped <= 2) {
    return {
      stimulusCount: 10,
      zoneCount: 1,
      goNoGo: false,
      hasDistractors: false,
      multiTarget: false,
      maxReactionMs: 2300,
      minDelayMs: 1000,
      maxDelayMs: 4000,
      goFraction: 1,
    };
  }
  if (clamped <= 4) {
    return {
      stimulusCount: 10,
      zoneCount: 3,
      goNoGo: false,
      hasDistractors: false,
      multiTarget: false,
      maxReactionMs: 2300,
      minDelayMs: 1000,
      maxDelayMs: 4000,
      goFraction: 1,
    };
  }
  if (clamped <= 6) {
    return {
      stimulusCount: 12,
      zoneCount: 3,
      goNoGo: true,
      hasDistractors: false,
      multiTarget: false,
      maxReactionMs: 1700,
      minDelayMs: 1000,
      maxDelayMs: 4000,
      goFraction: 0.6,
    };
  }
  if (clamped <= 8) {
    return {
      stimulusCount: 15,
      zoneCount: 3,
      goNoGo: true,
      hasDistractors: true,
      multiTarget: false,
      maxReactionMs: 1700,
      minDelayMs: 1000,
      maxDelayMs: 4000,
      goFraction: 0.6,
    };
  }
  return {
    stimulusCount: 15,
    zoneCount: 9,
    goNoGo: true,
    hasDistractors: true,
    multiTarget: true,
    maxReactionMs: 1700,
    minDelayMs: 1000,
    maxDelayMs: 4000,
    goFraction: 0.6,
  };
}

/** Zone center as percentage of container (for single-target). Zone 0 = center. */
export function getZoneCenterPct(zoneIndex: number): { xPct: number; yPct: number } {
  if (zoneIndex === 0) return { xPct: 50, yPct: 50 };
  // 3 zones: top-center, left, right
  if (zoneIndex === 1) return { xPct: 50, yPct: 30 };
  if (zoneIndex === 2) return { xPct: 25, yPct: 55 };
  if (zoneIndex === 3) return { xPct: 75, yPct: 55 };
  return { xPct: 50, yPct: 50 };
}

/** Generate trial sequence for the round (deterministic from seed). */
export function generateTrials(
  level: number,
  seed: string,
  roundIndex: number
): TrialSpec[] {
  const params = getLevelParams(level);
  const rng = seededRandom(`${seed}-${roundIndex}`);
  const trials: TrialSpec[] = [];
  const goCount = Math.round(params.stimulusCount * params.goFraction);

  let isGoMask: boolean[] | null = null;
  if (params.goNoGo) {
    if (goCount <= 0) {
      isGoMask = Array(params.stimulusCount).fill(false);
    } else if (goCount >= params.stimulusCount) {
      isGoMask = Array(params.stimulusCount).fill(true);
    } else {
      const mask = [
        ...Array(goCount).fill(true),
        ...Array(params.stimulusCount - goCount).fill(false),
      ] as boolean[];
      shuffleGoMask(mask, rng);
      capConsecutiveNogo(mask, MAX_CONSECUTIVE_NOGO);
      isGoMask = mask;
    }
  }

  for (let i = 0; i < params.stimulusCount; i++) {
    const isGo = isGoMask ? isGoMask[i] : true;
    if (params.multiTarget) {
      const positions = generateMultiTargetPositions(rng, isGo, params.hasDistractors);
      trials.push({
        type: isGo ? "go" : "nogo",
        zoneIndex: 0,
        positions,
      });
    } else {
      const zoneIndex =
        params.zoneCount === 1 ? 0 : pickInt(rng, 1, params.zoneCount);
      trials.push({
        type: isGo ? "go" : params.goNoGo ? "nogo" : "go",
        zoneIndex,
      });
    }
  }

  return trials;
}

function generateMultiTargetPositions(
  rng: () => number,
  hasGo: boolean,
  hasDistractors: boolean
): { xPct: number; yPct: number; type: StimulusType }[] {
  const count = 2 + Math.floor(rng() * 2);
  const positions: { xPct: number; yPct: number; type: StimulusType }[] = [];
  const centers = [
    [50, 35],
    [25, 55],
    [75, 55],
    [35, 70],
    [65, 70],
  ];
  const used = new Set<number>();
  for (let i = 0; i < count; i++) {
    let idx: number;
    do {
      idx = Math.floor(rng() * centers.length);
    } while (used.has(idx));
    used.add(idx);
    const [xPct, yPct] = centers[idx];
    let type: StimulusType = "nogo";
    if (i === 0 && hasGo) type = "go";
    else if (hasDistractors && rng() < 0.5) type = "distractor";
    positions.push({ xPct, yPct, type });
  }
  return positions;
}

/**
 * Per-round scoring for Reaction Tap.
 *
 * Accuracy uses the count of "miss-able" events (true go hits + false taps
 * on no-go + missed go targets) as denominator. Correctly withheld no-go
 * trials are not counted — a player that never taps anything would score 0,
 * not 100%.
 */
export interface ReactionTapTrialResult {
  type: StimulusType;
  /** True for a correct go tap. */
  correct: boolean;
  isNoGo: boolean;
  tapTime: number | null;
  reactionTime: number | null;
}

export interface ReactionTapRoundMetrics {
  accuracy: number;
  truePositives: number;
  falsePositives: number;
  missedMatches: number;
  avgReactionMs: number;
  reactionTimeMs: number | null;
  inhibitionErrors: number;
  missedTargets: number;
  errorCount: number;
}

export function computeReactionTapRoundMetrics(
  trialResults: ReactionTapTrialResult[],
): ReactionTapRoundMetrics {
  const truePositives = trialResults.filter(
    (r) => r.type === "go" && r.correct,
  ).length;
  const falsePositives = trialResults.filter(
    (r) => r.isNoGo && r.tapTime != null,
  ).length;
  const missedMatches = trialResults.filter(
    (r) => r.type === "go" && !r.correct,
  ).length;
  const denom = Math.max(1, truePositives + falsePositives + missedMatches);
  const accuracy = trialResults.length > 0 ? truePositives / denom : 0;

  const reactionTimes = trialResults
    .filter((r) => r.type === "go" && r.reactionTime != null)
    .map((r) => r.reactionTime as number);
  const avgReactionMs =
    reactionTimes.length > 0
      ? reactionTimes.reduce((a, b) => a + b, 0) / reactionTimes.length
      : 0;

  return {
    accuracy,
    truePositives,
    falsePositives,
    missedMatches,
    avgReactionMs,
    reactionTimeMs:
      reactionTimes.length > 0 ? Math.round(avgReactionMs) : null,
    inhibitionErrors: falsePositives,
    missedTargets: missedMatches,
    errorCount: falsePositives + missedMatches,
  };
}
